<script>
import UserAvatar from '@components/user/user-avatar.vue'
import baseMixin from '@mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import TheClientSelectorList from './the-client-selector-list.vue'

export default {
  name: 'ProfileMenuCard',
  components: {
    UserAvatar,
    TheClientSelectorList,
  },
  mixins: [baseMixin],
  data: () => {
    return {
      selectedClient: 0,
      clientSearch: '',
      expandClientSelector: false,
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/currentUser',
      userFullName: 'auth/currentUserFullName',
      userEmail: 'auth/currentUserEmail',
      isLoggedIn: 'auth/isUserLoggedIn',
      isLoadingAuth: 'auth/isLoadingAuth',
      hasMultipleClients: 'auth/hasMultipleClients',
      getClientLocations: 'auth/getClientLocations',
      getClient: 'auth/getClient',
      getSelectedClients: 'client/getSelectedClients',
    }),
    isClientSelectorEnabled() {
      return this.hasMultipleClients
    },
    compSelectedClientsText() {
      if (this.expandClientSelector)
        return 'Select locations from the below list'

      if (this.getSelectedClients.length === 1) {
        const client = this.getClient(this.getSelectedClients[0])
        return `Viewing: ${client?.clientName || 'Unknown'}`
      }

      return `Viewing data for ${this.getSelectedClients.length} locations`
    },
  },
  methods: {
    ...mapActions({
      logOut: 'auth/logOut',
    }),
    signOut() {
      this.logOut({ redirect: true, nuke: true })
    },
    closeClientSelector() {
      this.expandClientSelector = false
    },
  },
}
</script>

<template>
  <v-card v-if="isLoggedIn">
    <v-list>
      <v-list-item>
        <UserAvatar
          v-if="isLoggedIn"
          :badge-icon="isImpersonating ? 'mdi-incognito' : ''"
          badge-color="blue"
          :badge="isImpersonating"
          badge-offset-x="20"
          badge-offset-y="20"
          :name="userFullName"
          class="mr-4"
          size="lg"
        />
        <v-list-item-content class="pb-2">
          <v-list-item-title
            id="logged-in-user-name"
            :data-ad-user-name="userFullName"
            class="font-weight-medium"
            v-text="userFullName || ''"
          >
          </v-list-item-title>
          <v-list-item-subtitle
            :title="userEmail"
            class="font-weight-medium mb-2 white-space-normal text-overflow-unset"
            v-text="userEmail"
          >
          </v-list-item-subtitle>
          <v-list-item-subtitle>
            <a
              id="menu-logout-button"
              class="caption"
              link
              :title="$t('layout.profileMenuSignOutTooltipText')"
              @click="signOut"
            >
              {{ $t('layout.profileMenuSignOutButtonText') }}
            </a>
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <v-divider></v-divider>
    <v-list dense>
      <v-list-item @click="$router.push({ name: 'settings' })">
        <v-list-item-icon>
          <v-icon left>mdi-cog</v-icon>
        </v-list-item-icon>
        <v-list-item-content class="pb-2">
          <v-list-item-title>
            Settings
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <v-divider></v-divider>
    <v-list v-if="getSelectedClients.length > 0 && isClientSelectorEnabled">
      <v-list-item>
        <v-list-item-content class="pb-2">
          <v-list-item-title class="body-2">
            {{ compSelectedClientsText }}
          </v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn icon @click="expandClientSelector = !expandClientSelector"
            ><v-icon>{{
              expandClientSelector ? 'mdi-close' : 'mdi-filter-menu'
            }}</v-icon></v-btn
          >
        </v-list-item-action>
      </v-list-item>
    </v-list>
    <v-divider
      v-if="getSelectedClients.length > 0 && isClientSelectorEnabled"
    ></v-divider>
    <v-expand-transition v-if="expandClientSelector">
      <div class="grey lighten-5 pt-2">
        <v-text-field
          id="client-list-search"
          v-model="clientSearch"
          class="px-2 pb-2 pt-0"
          prepend-inner-icon="mdi-magnify"
          :placeholder="$t('client.clientSelectorSearchInputText')"
          hide-details
        ></v-text-field>
        <TheClientSelectorList
          :search="clientSearch"
          @onSave="closeClientSelector"
        />
      </div>
    </v-expand-transition>
    <v-card-actions
      v-if="$te('app.companyPrivacyURL')"
      class="d-flex justify-center"
    >
      <v-btn
        :href="$t('app.companyPrivacyURL')"
        target="_blank"
        text
        small
        class="rounded-lg"
      >
        {{ $t('general.privacyPolicyLinkText') }}
      </v-btn>
      <span
        v-if="$te('app.companyPrivacyURL') && $te('app.companyTermsURL')"
        aria-hidden="true"
        class="px-1"
      >
        •
      </span>
      <v-btn
        v-if="$te('app.companyTermsURL')"
        :href="$t('app.companyTermsURL')"
        target="_blank"
        text
        small
        class="rounded-lg"
      >
        {{ $t('general.termsLinkText') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
